import { useEffect, useMemo, useState } from 'react';
import type { CurrentUser } from '../api';
import { connectApi, type ConsentRequest } from '../api/connect';
import { t } from '../i18n';

/**
 * The consent page of the remote office connector (`/connect`). An assistant — claude.ai, Claude
 * Code — sent the browser here through `/api/oauth/authorize`; the signed-in human sees who asks
 * and decides. Both answers leave the page through the client's own redirect, so the page never
 * needs to know what happens next.
 */
export function Connect({ user }: { user: CurrentUser }) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';
  const [clientName, setClientName] = useState<string | null | 'invalid'>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!clientId || !redirectUri) {
      setClientName('invalid');
      return;
    }
    connectApi
      .consentInfo(clientId)
      .then((r) => setClientName('error' in r ? 'invalid' : r.client.name))
      .catch(() => setClientName('invalid'));
  }, [clientId, redirectUri]);

  const request = (): ConsentRequest => ({
    clientId,
    redirectUri,
    codeChallenge: params.get('code_challenge') ?? '',
    scope: (params.get('scope') ?? 'office').split(/\s+/).filter(Boolean),
    ...(params.get('state') ? { state: params.get('state')! } : {}),
    ...(params.get('resource') ? { resource: params.get('resource')! } : {}),
  });

  const allow = async () => {
    setBusy(true);
    setError(null);
    try {
      const { redirectTo } = await connectApi.consent(request());
      window.location.assign(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
      setBusy(false);
    }
  };

  const deny = async () => {
    setBusy(true);
    try {
      const { redirectTo } = await connectApi.deny({ redirectUri, ...(params.get('state') ? { state: params.get('state')! } : {}) });
      window.location.assign(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
      setBusy(false);
    }
  };

  if (clientName === null) return <div className="content muted">{t('common.loading')}</div>;
  if (clientName === 'invalid') return <div className="form narrow card error">{t('connect.invalid')}</div>;

  return (
    <div className="form narrow card">
      <h1>{t('connect.title')}</h1>
      <p>
        <strong>{clientName}</strong> {t('connect.asks')}
      </p>
      <p className="muted">{t('connect.as', { name: user.displayName, email: user.email })}</p>
      <ul className="muted">
        <li>{t('connect.scope.read')}</li>
        <li>{t('connect.scope.write')}</li>
        <li>{t('connect.scope.limits')}</li>
      </ul>
      <p className="muted">{t('connect.revoke')}</p>
      {error && <div className="error" role="alert">{error}</div>}
      <div className="row">
        <button className="btn" type="button" onClick={allow} disabled={busy}>{t('connect.allow')}</button>
        <button className="btn secondary" type="button" onClick={deny} disabled={busy}>{t('connect.deny')}</button>
      </div>
    </div>
  );
}
